// src/cockpit4/components/BossExtras.tsx
// ============================================
// 批次二 P1 决策深化组件（挂在「老板视图」内）
//   P1-④ 议题辩论室：三方观点（战略/风控/执行）+ 主席裁决，调 Hermes，落盘 Debates
//   P1-⑤ 一号位每日简报（老板摘要）：承诺兑现率 / 今日风险 / 需拍板事项，全部真实数据
// 铁律：数据全部来自 Vault 真实记录与 `/hermes-chat` 真实 AI 输出，禁止 mock。
// ============================================

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Gavel, Radar, UserRound, ShieldCheck, Zap, Loader2, ArrowRight,
  FileText, ListChecks, TrendingUp, AlertTriangle, Crown, BookCheck,
  Eye, Search, MessageSquareText, Users, UserCheck, Flame, XCircle,
} from "lucide-react";

const OPS = "10_Operations（运营层）";
const P_COMMIT = `${OPS}/Commitments/承诺账本.md`;
const P_RISK = `${OPS}/Risks/风险卡片.md`;
const P_DEBATE = `${OPS}/Debates`;

async function readMd(relPath: string): Promise<string> {
  try {
    const r = await fetch(`/vault-api/read?path=${encodeURIComponent(relPath)}`);
    if (!r.ok) return "";
    const j = await r.json();
    return j?.success && typeof j.content === "string" ? j.content : "";
  } catch { return ""; }
}

async function writeMd(relPath: string, content: string): Promise<boolean> {
  try {
    const r = await fetch(`/vault-api/write?path=${encodeURIComponent(relPath)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content }),
    });
    const j = await r.json();
    return !!j?.success;
  } catch { return false; }
}

/** 解析承诺账本 markdown → 承诺行 */
function parseCommitments(md: string): { person: string; content: string; due: string; status: string }[] {
  const out: { person: string; content: string; due: string; status: string }[] = [];
  for (const ln of md.split("\n")) {
    const t = ln.trim();
    if (!t.startsWith("|")) continue;
    if (t.includes("---")) continue;
    const c = t.split("|").map((s) => s.trim());
    const person = c[1]?.replace(/\*\*/g, "").trim();
    const content = c[2]?.trim();
    const due = c[3]?.trim();
    const status = c[5]?.trim();
    if (!person || !content) continue;
    out.push({ person, content, due, status });
  }
  return out;
}

// ============================================
// P1-⑤ 一号位每日简报（老板摘要）
// ============================================
export function BossSummary() {
  const [commits, setCommits] = useState<ReturnType<typeof parseCommitments>>([]);
  const [risks, setRisks] = useState<{ title: string; status: string }[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [cm, rk] = await Promise.all([readMd(P_COMMIT), readMd(P_RISK)]);
    setCommits(parseCommitments(cm));
    setRisks(
      rk.split("\n")
        .filter((ln) => ln.trim().startsWith("|"))
        .filter((ln) => !ln.includes("---"))
        .map((ln) => {
          const c = ln.split("|").map((s) => s.trim());
          return { title: c[3] || "", status: c[1] || "open" };
        })
        .filter((r) => r.title)
    );
    setLoading(false);
  };

  useEffect(() => { void load(); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const total = commits.length;
  const done = commits.filter((c) => c.status === "done").length;
  const overdue = commits.filter((c) => c.status !== "done" && c.due && c.due < today);
  const openRisks = risks.filter((r) => r.status === "open").slice(0, 3);
  const rate = total ? Math.round((done / total) * 100) : 0;

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 text-[10px] font-mono text-amber-300 uppercase tracking-wider">
          <Crown size={11} /> 一号位每日简报 · 老板摘要
        </div>
        <p className="mt-1 text-[10px] text-gray-500">数据源：承诺账本对账 + 风险卡片（真实 Vault 记录）。承诺兑现率按截止与验收标准判定。</p>
      </div>

      {loading ? (
        <div className="py-8 text-center text-[11px] font-mono text-gray-600 flex items-center justify-center gap-2">
          <Loader2 size={12} className="animate-spin" /> 汇总真实经营数据…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Metric label="承诺总数" val={String(total)} c="#38BDF8" />
            <Metric label="已兑现" val={String(done)} c="#34D399" />
            <Metric label="兑现率" val={`${rate}%`} c="#A78BFA" />
            <Metric label="逾期风险" val={String(overdue.length)} c="#FB7185" />
          </div>

          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 space-y-2">
            <div className="text-[10px] font-mono text-cyan-300 uppercase tracking-wider flex items-center gap-1.5">
              <ListChecks size={11} /> 需老板拍板 / 关注事项（≤3）
            </div>
            {overdue.length === 0 && openRisks.length === 0 && (
              <div className="text-[11px] font-mono text-gray-600">今日暂无逾期承诺与未关闭风险。</div>
            )}
            {overdue.slice(0, 3).map((c, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px] text-rose-200">
                <AlertTriangle size={11} className="text-rose-400 flex-shrink-0" />
                <span className="truncate">{c.person} · {c.content}</span>
                <span className="ml-auto flex-shrink-0 font-mono text-[9px] text-rose-400">逾期截止 {c.due}</span>
              </div>
            ))}
            {openRisks.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px] text-amber-200">
                <ShieldCheck size={11} className="text-amber-400 flex-shrink-0" />
                <span className="truncate">{r.title}</span>
                <span className="ml-auto flex-shrink-0 font-mono text-[9px] text-amber-400">未关闭</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Metric({ label, val, c }: { label: string; val: string; c: string }) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">{label}</div>
      <div className="text-xl font-bold" style={{ color: c }}>{val}</div>
    </div>
  );
}

// ============================================
// P1-① 上帝视角问答（老板问答）
// ============================================
const ONTO_FILES = ["客户", "项目", "承诺", "信源", "内容资产", "供应商"];

export function BossQA() {
  const [ctx, setCtx] = useState("");
  const [ctxReady, setCtxReady] = useState(false);
  const [q, setQ] = useState("");
  const [thinking, setThinking] = useState(false);
  const [answer, setAnswer] = useState("");
  const [err, setErr] = useState("");

  const buildContext = async (): Promise<string> => {
    const chunks: string[] = [];
    // Ontology 6 类实体（地图）
    for (const f of ONTO_FILES) {
      const md = await readMd(`09_Knowledge（知识中心）/Ontology/${f}-index.md`);
      if (md) {
        const body = md.replace(/```/g, "");
        chunks.push(`【${f}（Ontology）】\n${body.slice(0, 500)}`);
      }
    }
    // 决策层：承诺账本 + 风险卡片（真实）
    const cm = await readMd(P_COMMIT);
    if (cm) chunks.push(`【承诺账本】\n${cm.slice(0, 700)}`);
    const rk = await readMd(P_RISK);
    if (rk) chunks.push(`【风险卡片】\n${rk.slice(0, 600)}`);
    return chunks.join("\n\n");
  };

  useEffect(() => {
    void (async () => {
      const c = await buildContext();
      setCtx(c);
      setCtxReady(true);
    })();
  }, []);

  const ask = async (question: string) => {
    if (!question.trim()) return;
    if (!ctxReady) setErr("经营上下文仍在装载，请稍候");
    setThinking(true); setErr(""); setAnswer("");
    try {
      const prompt =
        "你是这家企业的「上帝视角老板 AI」参谋（对标零一万物·老板AI）。基于下面【真实的经营上下文】（客户/项目/承诺/信源/内容资产/供应商 + 承诺账本 + 风险卡片），回答老板的提问。\n" +
        "要求：结论先行 + 关键数据 + 每条结论尽量指明它来自哪份上下文来源；若上下文不足以回答，明确说“依据当前记录不足”，不要编造。\n\n" +
        `【经营上下文】\n${ctx.slice(0, 3200)}\n\n` +
        `【老板提问】${question.trim()}`;
      const r = await fetch("/hermes-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: prompt }),
      });
      const d = await r.json().catch(() => null);
      setAnswer(d?.reply ?? d?.content ?? "未返回回答，请稍后再试");
    } catch (e) {
      setErr(`问答失败：${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setThinking(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 space-y-2">
        <div className="flex items-center gap-2 text-[10px] font-mono text-sky-300 uppercase tracking-wider">
          <Eye size={11} /> 上帝视角问答 · 老板 AI
        </div>
        <p className="text-[10px] text-gray-500">
          {ctxReady
            ? `已装载真实经营上下文（6 类 Ontology + 承诺账本 + 风险卡片，${ctx.length} 字符）`
            : "正在装载真实经营上下文…"}
        </p>
        <div className="flex gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void ask(q)}
            placeholder="问任何经营问题，如：当前最大的风险是什么？哪类客户价值最高？"
            className="flex-1 px-2.5 py-1.5 rounded-md border border-white/[0.08] bg-black/20 text-[11px] text-white outline-none focus:border-sky-500/40"
          />
          <button
            onClick={() => void ask(q)}
            disabled={thinking}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-sky-500/20 text-sky-200 text-[11px] font-mono hover:bg-sky-500/30 transition-colors disabled:opacity-50 flex-shrink-0"
          >
            {thinking ? <Loader2 size={11} className="animate-spin" /> : <Search size={11} />} 问老板 AI
          </button>
        </div>
        {err && <div className="text-[10px] font-mono text-rose-400">{err}</div>}
      </div>

      {answer && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2 text-[10px] font-mono text-gray-400 uppercase tracking-wider">
            <MessageSquareText size={11} className="text-sky-400" /> 老板 AI 回答
          </div>
          <pre className="whitespace-pre-wrap text-[11px] leading-relaxed text-gray-300 font-sans">{answer}</pre>
        </motion.div>
      )}
    </div>
  );
}

// ============================================
// P1-④ 议题辩论室
// ============================================
export function DebateRoom() {
  const [issue, setIssue] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState("");
  const [saved, setSaved] = useState(false);
  const [toast, setToast] = useState("");
  const today = new Date().toISOString().slice(0, 10);

  const run = async () => {
    if (!issue.trim()) { setToast("请先输入议题"); return; }
    setRunning(true); setSaved(false); setResult("");
    try {
      const resp = await fetch("/hermes-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content:
            "你是企业决策委员会主席。请对以下议题进行多角色裁决：战略（可复制/竞争优势）、风控（最坏情况/止损）、执行（落地路径/资源配置）三方各 2-3 条观点，最后由主席裁决给出明确结论与下一步（含责任人与期限建议）。\n" +
            "议题：" + issue.trim() + "\n" +
            "输出格式：\n【战略】...\n【风控】...\n【执行】...\n【主席裁决】...",
        }),
      });
      const d = await resp.json().catch(() => null);
      setResult(d?.reply ?? d?.content ?? "辩论未返回结果，请稍后重试");
    } catch (e) {
      setResult(`辩论失败：${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setRunning(false);
    }
  };

  const save = async () => {
    const ts = new Date().toISOString().slice(0, 16).replace("T", " ");
    const md =
      `---\ntype: debate\ndate: ${today}\nissue: ${issue.trim()}\n---\n\n# 议题辩论：${issue.trim()}\n\n${result}\n\n---\n记录时间：${ts}\n`;
    const fn = `议题-${Date.now()}.md`;
    const ok = await writeMd(`${P_DEBATE}/${fn}`, md);
    if (ok) { setSaved(true); setToast("已写入辩论纪要"); } else { setToast("落盘失败"); }
    setTimeout(() => setToast(""), 2200);
  };

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 space-y-2">
        <div className="flex items-center gap-2 text-[10px] font-mono text-violet-300 uppercase tracking-wider">
          <Gavel size={11} /> 议题辩论室 · 多角色裁决
        </div>
        <div className="flex gap-2">
          <input
            value={issue}
            onChange={(e) => setIssue(e.target.value)}
            placeholder="输入待裁决议题，如：是否接入 XX 渠道 / 是否上调产品定价"
            className="flex-1 px-2.5 py-1.5 rounded-md border border-white/[0.08] bg-black/20 text-[11px] text-white outline-none focus:border-violet-500/40"
          />
          <button
            onClick={() => void run()}
            disabled={running}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-violet-500/20 text-violet-200 text-[11px] font-mono hover:bg-violet-500/30 transition-colors disabled:opacity-50 flex-shrink-0"
          >
            {running ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />} 展开辩论
          </button>
        </div>
        <div className="grid grid-cols-3 gap-1.5 text-[9px] font-mono text-gray-500">
          <span className="flex items-center gap-1"><Radar size={9} className="text-sky-400" />战略</span>
          <span className="flex items-center gap-1"><ShieldCheck size={9} className="text-rose-400" />风控</span>
          <span className="flex items-center gap-1"><UserRound size={9} className="text-emerald-400" />执行</span>
        </div>
        {toast && <div className="text-[10px] font-mono text-emerald-400">{toast}</div>}
      </div>

      {result && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <FileText size={11} className="text-violet-400" />
            <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider">辩论纪要</span>
            <div className="flex-1" />
            <button
              onClick={() => void save()}
              disabled={saved}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-mono transition-colors ${
                saved ? "bg-emerald-500/20 text-emerald-300" : "bg-white/[0.05] text-gray-300 hover:bg-white/[0.1]"
              }`}
            >
              <ArrowRight size={10} /> {saved ? "已落盘" : "落盘决策纪要"}
            </button>
          </div>
          <pre className="whitespace-pre-wrap text-[11px] leading-relaxed text-gray-300 font-sans">{result}</pre>
        </motion.div>
      )}
    </div>
  );
}

// ============================================
// P1-⓪ 人才盘点（李开复「老板 AI · 人才盘点」补缺）
// ============================================
// 对标：零一万物·老板AI六大能力之一「人才盘点」。把「人」作为第一资产与财报因子：
//   谁在产出（承诺兑现）、谁在滞后（逾期/风险暴露）、谁在空转（有诺未动/无诺）。
// 铁律：纯真实数据聚合（承诺账本 person 主键 × 风险卡片标题中的责任人），禁止 mock。
export function TalentReview({
  commitments,
  risks,
}: {
  commitments: { person: string; content: string; due: string; status: string; source?: string }[];
  risks: { title: string; level: string; status: string }[];
}) {
  const today = new Date().toISOString().slice(0, 10);

  // 按人聚合：承诺明细 + 关联风险
  const rows = (() => {
    const map = new Map<string, { c: typeof commitments; r: typeof risks }>();
    for (const c of commitments) {
      const k = c.person?.trim() || "未指定";
      if (!map.has(k)) map.set(k, { c: [], r: [] });
      map.get(k)!.c.push(c);
    }
    for (const risk of risks) {
      // 优先解析「承诺逾期：{负责人} · …」这种标题里的负责人
      const m = risk.title.match(/[：:]\s*([^·　\s]{2,12})/);
      let hit = m ? m[1].trim() : "";
      if (!hit) {
        // 回退：标题含某位在册人员名即归属该人
        for (const k of map.keys()) if (k !== "未指定" && risk.title.includes(k)) { hit = k; break; }
      }
      if (hit && map.has(hit)) map.get(hit)!.r.push(risk);
    }
    return [...map.entries()].map(([person, v]) => {
      const total = v.c.length;
      const done = v.c.filter((x) => x.status === "done").length;
      const risk = v.c.filter((x) => x.status === "risk").length;
      const overdue = v.c.filter((x) => x.status !== "done" && x.due && x.due < today).length;
      const open = v.c.filter((x) => x.status === "open").length;
      const rate = total ? Math.round((done / total) * 100) : 0;
      const riskHits = v.r.filter((x) => x.status === "open").length;
      // 人效红灯：兑现率 < 67% 且存在未关闭风险，或逾期 >=2
      const redFlag = (rate < 67 && riskHits > 0) || overdue >= 2;
      // 评语（规则式，真实数据驱动，非 AI 捏造）
      let verdict = "";
      if (!total) verdict = "在册但暂无承诺 — 产出待记录";
      else if (redFlag) verdict = "⚠ 人效红灯：高逾期 + 风险暴露，需老板介入追责";
      else if (rate >= 90 && riskHits === 0) verdict = "✓ 核心执行者：稳定兑现，可委以重任";
      else if (rate >= 67) verdict = "→ 正常执行者：大体兑现，仍有个别项待闭环";
      else verdict = "→ 滞后执行者：兑现率偏低，需重点跟进";
      return { person, total, done, risk, overdue, open, rate, riskHits, redFlag, verdict };
    });
  })();

  // 排序：红灯在前，其次兑现率升序（最滞后的排前，让老板先看问题）
  const sorted = [...rows].sort((a, b) =>
    Number(b.redFlag) - Number(a.redFlag) || a.rate - b.rate
  );

  const [deepLoading, setDeepLoading] = useState(false);
  const [deepResult, setDeepResult] = useState("");

  // 用人建议：把真实人才盘点数据喂给 AI 深化决策（/hermes-chat 已修正 content 字段）
  const deepAdvice = async () => {
    setDeepLoading(true); setDeepResult("");
    const brief = sorted
      .map((p) => `${p.person}：${p.done}/${p.total} 兑现(${p.rate}%)，${p.overdue}逾期，${p.riskHits}条未关风险`)
      .join("；");
    try {
      const resp = await fetch("/hermes-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content:
            "你是企业 CHRO + COO。基于下面【真实的人才盘点数据】（承诺账本兑现率 × 风险暴露），给出用人建议：谁该升责、谁该跟进、谁该调整，以及如何提升整体兑现率。\n" +
            "要求：结论文本控制在 180 字内，每条指明对应的人，不要编造数据。\n\n数据：" + brief,
        }),
      });
      const d = await resp.json().catch(() => null);
      setDeepResult(d?.reply ?? d?.content ?? "AI 未返回用人建议，请稍后重试");
    } catch (e) {
      setDeepResult(`用人建议失败：${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setDeepLoading(false);
    }
  };

  const redCount = sorted.filter((r) => r.redFlag).length;
  const avgRate = rows.length ? Math.round(rows.reduce((a, r) => a + r.rate, 0) / rows.length) : 0;

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 text-[10px] font-mono text-cyan-300 uppercase tracking-wider">
          <Users size={11} /> 人才盘点 · 人员资产
        </div>
        <p className="mt-1 text-[10px] text-gray-500">
          按「承诺账本 × 风险卡片」聚合真实人效：谁在兑现、谁在滞后、谁需要老板介入。人效是"能否改变财报"的第一因子。
        </p>
      </div>

      {rows.length === 0 ? (
        <div className="px-3 py-10 text-center text-[11px] font-mono text-gray-600">
          暂无人员产出数据 — 在「承诺账本」录入第一条真实承诺后自动生成人才盘点评级
        </div>
      ) : (
        <>
          {/* 概览 */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <Metric label="在册人员" val={String(rows.length)} c="#38BDF8" />
            <Metric label="人均兑现率" val={`${avgRate}%`} c="#A78BFA" />
            <Metric label="人效红灯" val={String(redCount)} c="#FB7185" />
          </div>

          {/* 用人建议 */}
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 space-y-2">
            <div className="flex items-center gap-2">
              <div className="flex-1 text-[10px] font-mono text-gray-300 uppercase tracking-wider flex items-center gap-1.5">
                <Zap size={11} className="text-cyan-400" /> 用人建议（AI 深化，基于真实盘点数据）
              </div>
              <button
                onClick={() => void deepAdvice()}
                disabled={deepLoading}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/[0.05] text-gray-300 text-[10px] font-mono hover:bg-white/[0.1] disabled:opacity-50"
              >
                {deepLoading ? <Loader2 size={10} className="animate-spin" /> : <ArrowRight size={10} />} 问用人 AI
              </button>
            </div>
            {deepResult && <pre className="whitespace-pre-wrap text-[11px] leading-relaxed text-gray-300 font-sans">{deepResult}</pre>}
          </div>

          {/* 人员明细（红灯 → 滞后 → 正常/核心） */}
          <div className="space-y-2">
            {sorted.map((p) => (
              <motion.div
                key={p.person}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                className={`rounded-xl border p-3 ${
                  p.redFlag
                    ? "border-rose-500/30 bg-rose-500/[0.05]"
                    : p.rate < 100
                    ? "border-amber-500/20 bg-white/[0.02]"
                    : "border-emerald-500/20 bg-white/[0.02]"
                }`}
              >
                <div className="flex items-center gap-2">
                  {p.redFlag ? (
                    <Flame size={13} className="text-rose-400 flex-shrink-0" />
                  ) : p.rate >= 90 ? (
                    <UserCheck size={13} className="text-emerald-400 flex-shrink-0" />
                  ) : (
                    <UserRound size={13} className="text-cyan-400 flex-shrink-0" />
                  )}
                  <span className="text-[12px] font-semibold text-white">{p.person}</span>
                  {p.redFlag && (
                    <span className="px-1.5 py-0.5 rounded-full text-[9px] font-mono bg-rose-500/20 text-rose-300">人效红灯</span>
                  )}
                  <div className="flex-1" />
                  <div className="flex items-center gap-3 text-[10px] font-mono">
                    <span className="text-gray-400">{p.done}/{p.total} 兑现</span>
                    <span className={p.rate >= 67 ? "text-emerald-300" : "text-rose-300"}>{p.rate}%</span>
                    <span className="text-gray-500">{p.overdue} 逾期</span>
                    <span className={p.riskHits > 0 ? "text-rose-300" : "text-gray-500"}>{p.riskHits} 风险</span>
                  </div>
                </div>
                {/* 兑现率条 */}
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${Math.max(4, p.rate)}%`,
                        background:
                          p.rate >= 90 ? "#34d399" : p.rate >= 67 ? "#22d3ee" : "#fb7185",
                      }}
                    />
                  </div>
                  <span className={`text-[10px] font-mono ${p.redFlag ? "text-rose-300" : "text-gray-400"}`}>{p.verdict}</span>
                </div>
                {/* 明细条目 */}
                {p.total > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {commitments.filter((c) => c.person?.trim() === p.person).slice(0, 4).map((c, i) => (
                      <span
                        key={i}
                        className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono ${
                          c.status === "done"
                            ? "bg-emerald-500/15 text-emerald-300"
                            : c.status === "risk" || (c.due && c.due < today)
                            ? "bg-rose-500/15 text-rose-300"
                            : "bg-sky-500/15 text-sky-300"
                        }`}
                      >
                        {c.status === "done" ? <UserCheck size={8} /> : <XCircle size={8} />}
                        {c.content.slice(0, 20)}
                      </span>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
// ============================================
// P1-⑥ 获客 → 收入漏斗
// ============================================
export function FunnelPanel() {
  const [dims, setDims] = useState<{ label: string; value: number }[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const out: { label: string; value: number }[] = [];
    try {
      // 服务端聚合：/funnel-api 串起 官网访问→GEO流量→独立访客→留资线索（口令在后端，不进 bundle）
      const fr = await fetch(`/funnel-api?days=30`).catch(() => null);
      if (fr && fr.ok) {
        const f = await fr.json().catch(() => null);
        const dims = f?.dims;
        if (f?.success && Array.isArray(dims)) {
          for (const d of dims) {
            out.push({ label: d.label ?? "维度", value: d.value ?? 0 });
          }
        }
      }
      // CRM 流水线：读真实指标（存在则并入，接口未开放不硬编码）
      const cr = await fetch(`/crm-api?action=pipeline`).catch(() => null);
      if (cr && cr.ok) {
        const c = await cr.json().catch(() => null);
        const stages: Record<string, number> = c?.stages ?? c?.pipeline ?? {};
        const labels: Record<string, string> = {
          lead: "线索", opportunity: "商机", quotation: "报价",
          contract: "签约", invoice: "开票", payment: "回款", project: "项目", renew: "续约",
        };
        for (const [k, v] of Object.entries(stages)) {
          out.push({ label: labels[k] ?? k, value: Number(v) || 0 });
        }
      }
    } catch { /* 数据接口不可用时仅显示已获得的部分 */ }
    setDims(out);
    setLoading(false);
  };

  useEffect(() => { void load(); }, []);

  const max = Math.max(1, ...dims.map((d) => d.value));

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 text-[10px] font-mono text-emerald-300 uppercase tracking-wider">
          <TrendingUp size={11} /> 获客 → 收入漏斗
        </div>
        <p className="mt-1 text-[10px] text-gray-500">把 GEO 可见性 × CRM 全流水线串成漏斗。数据来自真实接口，接口未开放阶段自动折叠。</p>
      </div>

      {loading ? (
        <div className="py-8 text-center text-[11px] font-mono text-gray-600 flex items-center justify-center gap-2">
          <Loader2 size={12} className="animate-spin" /> 聚合漏斗数据…
        </div>
      ) : dims.length === 0 ? (
        <div className="py-8 text-center text-[11px] font-mono text-gray-600">
          暂无漏斗数据 — CRM 流水线接口未返回结构，仅展示可用维度。
        </div>
      ) : (
        <div className="space-y-1.5">
          {dims.map((d, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-24 text-right text-[10px] font-mono text-gray-400 flex-shrink-0">{d.label}</div>
              <div className="flex-1 h-7 rounded-md bg-white/[0.02] overflow-hidden">
                <div
                  className="h-full rounded-md flex items-center justify-end pr-2"
                  style={{
                    width: `${Math.max(6, (d.value / max) * 100)}%`,
                    background: `linear-gradient(90deg, #22d3ee55, #22d3eecc)`,
                  }}
                >
                  <span className="text-[10px] font-bold text-white">{d.value}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}